import axios from 'axios';

const api = axios.create({
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('orders_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('orders_token');
      localStorage.removeItem('orders_user');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ====== Auth ======
export const apiLogin = (username, password) => api.post('/api/auth/login', { username, password });
export const apiMe = () => api.get('/api/auth/me');

// ====== Users ======
export const getUsers = (includeInactive = false) => api.get(`/api/users${includeInactive ? '?include_inactive=1' : ''}`);
export const createUser = (data) => api.post('/api/users', data);
export const updateUser = (id, data) => api.put(`/api/users/${id}`, data);
export const deleteUser = (id, hard = false) => api.delete(`/api/users/${id}${hard ? '?hard=1' : ''}`);
export const getUserAssignments = (id) => api.get(`/api/users/${id}/assignments`);
export const updateUserAssignments = (id, data) => api.put(`/api/users/${id}/assignments`, data);

// ====== Shops ======
export const getShops = (includeInactive = false) => api.get(`/api/shops${includeInactive ? '?include_inactive=1' : ''}`);
export const getMyShops = () => api.get('/api/shops/mine');
export const createShop = (data) => api.post('/api/shops', data);
export const updateShop = (id, data) => api.put(`/api/shops/${id}`, data);
export const deleteShop = (id, hard = false) => api.delete(`/api/shops/${id}${hard ? '?hard=1' : ''}`);
export const reorderShops = (ids) => api.put('/api/shops/reorder/batch', { ids });

// ====== Factories ======
// shopId filters out the factories that shop isn't allowed to order from
export const getFactories = (includeInactive = false, shopId = null) => {
  const params = [];
  if (includeInactive) params.push('include_inactive=1');
  if (shopId) params.push(`shop_id=${shopId}`);
  return api.get(`/api/factories${params.length ? '?' + params.join('&') : ''}`);
};
export const getMyFactories = () => api.get('/api/factories/mine');
export const createFactory = (data) => api.post('/api/factories', data);
export const updateFactory = (id, data) => api.put(`/api/factories/${id}`, data);
export const deleteFactory = (id, hard = false) => api.delete(`/api/factories/${id}${hard ? '?hard=1' : ''}`);
export const reorderFactories = (ids) => api.put('/api/factories/reorder/batch', { ids });

// ====== Products ======
export const getProducts = (opts = {}) => {
  const params = [];
  if (opts.includeInactive) params.push('include_inactive=1');
  if (opts.factoryId) params.push(`factory_id=${opts.factoryId}`);
  // Hides products this shop isn't allowed to sell (franchise visibility)
  if (opts.shopId) params.push(`shop_id=${opts.shopId}`);
  return api.get(`/api/products${params.length ? '?' + params.join('&') : ''}`);
};
export const createProduct = (data) => api.post('/api/products', data);
export const updateProduct = (id, data) => api.put(`/api/products/${id}`, data);
export const deleteProduct = (id, hard = false) => api.delete(`/api/products/${id}${hard ? '?hard=1' : ''}`);
// Accepts either a plain array of ids (old call sites) or
// [{ id, category_id }], which also moves products between categories.
export const reorderProducts = (list) => {
  if (list.length > 0 && typeof list[0] === 'object') {
    return api.put('/api/products/reorder/batch', { items: list });
  }
  return api.put('/api/products/reorder/batch', { ids: list });
};

// ====== Categories ======
export const getCategories = (opts = {}) => {
  const params = [];
  if (opts.includeInactive) params.push('include_inactive=1');
  // Drops categories this shop isn't allowed to sell from
  if (opts.shopId) params.push(`shop_id=${opts.shopId}`);
  return api.get(`/api/categories${params.length ? '?' + params.join('&') : ''}`);
};
export const createCategory = (data) => api.post('/api/categories', data);
export const updateCategory = (id, data) => api.put(`/api/categories/${id}`, data);
export const deleteCategory = (id, force = false) => api.delete(`/api/categories/${id}${force ? '?force=1' : ''}`);
export const reorderCategories = (ids) => api.put('/api/categories/reorder/batch', { ids });

// ====== Orders ======
export const createOrder = (data) => api.post('/api/orders', data);
export const getOrders = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.shop_id) qs.set('shop_id', params.shop_id);
  if (params.factory_id) qs.set('factory_id', params.factory_id);
  if (params.since) qs.set('since', params.since);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  // 'requested_for' makes from/to apply to the pickup date instead of
  // when the order was placed
  if (params.date_field) qs.set('date_field', params.date_field);
  // These three were missing before, which silently disabled server-side
  // search and pagination on the "Οι Παραγγελίες μου" page.
  if (params.search) qs.set('search', params.search);
  if (params.page) qs.set('page', params.page);
  if (params.page_size) qs.set('page_size', params.page_size);
  const s = qs.toString();
  return api.get(`/api/orders${s ? '?' + s : ''}`);
};
export const getOrder = (id) => api.get(`/api/orders/${id}`);
// Only DELIVERED and CANCELLED go through here now. IN_PROGRESS and READY
// are derived from the items by the backend.
export const updateOrderStatus = (id, to_status, note) => api.put(`/api/orders/${id}/status`, { to_status, note });

// One factory's board: its orders, with only its own items, in one request
export const getFactoryQueue = (factoryId, includeDone = false) =>
  api.get(`/api/orders/factory-queue?factory_id=${factoryId}${includeDone ? '&include_done=1' : ''}`);
// Ξεκινώ for this factory's share of an order
export const startOrderPart = (orderId, factoryId, started_by_worker_id) =>
  api.post(`/api/orders/${orderId}/parts/${factoryId}/start`, { started_by_worker_id });
// Tick / untick one product as finished
export const setItemReady = (orderId, itemId, ready, worker_id) =>
  api.put(`/api/orders/${orderId}/items/${itemId}/ready`, { ready, worker_id: worker_id || null });
// "Κατάλαβα" on a cancelled order — clears it from this factory's board
export const ackCancelledPart = (orderId, factoryId) =>
  api.post(`/api/orders/${orderId}/parts/${factoryId}/cancel-seen`);
// Hand one product to a different factory
export const moveItemFactory = (orderId, itemId, factory_id) =>
  api.put(`/api/orders/${orderId}/items/${itemId}/factory`, { factory_id });
// Move the pickup to a different shop
export const moveOrderShop = (orderId, shop_id) =>
  api.put(`/api/orders/${orderId}/shop`, { shop_id });

// ====== Order photos ======
export const getOrderImages = (orderId) => api.get(`/api/orders/${orderId}/images`);
// data_url comes from fileToResizedDataUrl() — already shrunk in the browser
export const uploadOrderImage = (orderId, data_url) =>
  api.post(`/api/orders/${orderId}/images`, { data_url });
export const deleteOrderImage = (orderId, imageId) =>
  api.delete(`/api/orders/${orderId}/images/${imageId}`);
// Edit an existing order (only allowed while it is still NEW — the backend
// re-checks and answers 409 if the factory has already started it)
export const updateOrder = (id, data) => api.put(`/api/orders/${id}`, data);

// ====== Announcements ======
export const getAnnouncements = (opts = {}) => {
  const params = [];
  if (opts.limit) params.push(`limit=${opts.limit}`);
  if (opts.includeInactive) params.push('include_inactive=1');
  return api.get(`/api/announcements${params.length ? '?' + params.join('&') : ''}`);
};
export const createAnnouncement = (data) => api.post('/api/announcements', data);
export const updateAnnouncement = (id, data) => api.put(`/api/announcements/${id}`, data);
export const deleteAnnouncement = (id, hard = false) =>
  api.delete(`/api/announcements/${id}${hard ? '?hard=1' : ''}`);

// ====== Dashboard ======
export const getDashboard = () => api.get('/api/dashboard');

// ====== Workers ======
export const getWorkers = (includeInactive = false) => api.get(`/api/workers${includeInactive ? '?include_inactive=1' : ''}`);
export const getWorkersByShop = (shopId) => api.get(`/api/workers/by-shop/${shopId}`);
export const getWorkersByFactory = (factoryId) => api.get(`/api/workers/by-factory/${factoryId}`);
export const createWorker = (data) => api.post('/api/workers', data);
export const updateWorker = (id, data) => api.put(`/api/workers/${id}`, data);
export const deleteWorker = (id, hard = false) => api.delete(`/api/workers/${id}${hard ? '?hard=1' : ''}`);
export const reorderWorkers = (ids) => api.put('/api/workers/reorder/batch', { ids });

export default api;
